import axios from 'axios';
import {
  MOCK_SCENARIOS_RESPONSE,
  MOCK_SCENARIO_ID,
  MOCK_SUBMISSION_ID,
  buildMockSubmissionDetail,
  buildMockSubmissionsList,
  isMockSubmissionId,
  mockSubmissionIdForScenario,
  shouldUseTestingExercisingMock,
  normalizeSubmissionsPayload,
  delay,
} from './mockData';

const API_BASE = process.env.REACT_APP_API_BASE;

export const types = {
  REQUEST_SCENARIOS: 'testingExercising/REQUEST_SCENARIOS',
  RECEIVE_SCENARIOS: 'testingExercising/RECEIVE_SCENARIOS',
  REQUEST_SCENARIO_SUBMISSIONS: 'testingExercising/REQUEST_SCENARIO_SUBMISSIONS',
  RECEIVE_SCENARIO_SUBMISSIONS: 'testingExercising/RECEIVE_SCENARIO_SUBMISSIONS',
  REQUEST_SUBMISSION_DETAIL: 'testingExercising/REQUEST_SUBMISSION_DETAIL',
  RECEIVE_SUBMISSION_DETAIL: 'testingExercising/RECEIVE_SUBMISSION_DETAIL',
  SET_SUBMISSION_REQUEST: 'testingExercising/SET_SUBMISSION_REQUEST',
  SET_USING_MOCK: 'testingExercising/SET_USING_MOCK',
};

const requestScenarios = () => ({ type: types.REQUEST_SCENARIOS });
const receiveScenarios = (payload) => ({ type: types.RECEIVE_SCENARIOS, payload });
const requestScenarioSubmissions = () => ({ type: types.REQUEST_SCENARIO_SUBMISSIONS });
const receiveScenarioSubmissions = (payload) => ({ type: types.RECEIVE_SCENARIO_SUBMISSIONS, payload });
const requestSubmissionDetail = () => ({ type: types.REQUEST_SUBMISSION_DETAIL });
const receiveSubmissionDetail = (payload) => ({ type: types.RECEIVE_SUBMISSION_DETAIL, payload });
const setSubmissionRequest = (value) => ({ type: types.SET_SUBMISSION_REQUEST, value });
const setUsingMock = (value) => ({ type: types.SET_USING_MOCK, value });

function isForcedMock() {
  const flag = String(process.env.REACT_APP_TESTING_EXERCISING_MOCK || '').toLowerCase();
  return flag === 'true' || flag === '1';
}

function isMockDisabled() {
  const flag = String(process.env.REACT_APP_TESTING_EXERCISING_MOCK || '').toLowerCase();
  return flag === 'false' || flag === '0';
}

/** GET /plans/{planId}/scenarios */
export const fetchScenarios = (planId) => async (dispatch) => {
  dispatch(requestScenarios());

  if (isForcedMock()) {
    await delay();
    dispatch(setUsingMock(true));
    dispatch(receiveScenarios(MOCK_SCENARIOS_RESPONSE));
    return MOCK_SCENARIOS_RESPONSE;
  }

  const url = `${API_BASE}plans/${planId}/scenarios`;
  try {
    const res = await axios.get(url);
    const data = res.data || {};
    if (shouldUseTestingExercisingMock(data)) {
      dispatch(setUsingMock(true));
      dispatch(receiveScenarios(MOCK_SCENARIOS_RESPONSE));
      return MOCK_SCENARIOS_RESPONSE;
    }
    // Prefer live API payload as returned (no mock media enrichment).
    dispatch(setUsingMock(false));
    dispatch(receiveScenarios(data));
    return data;
  } catch (err) {
    if (isMockDisabled()) {
      dispatch(setUsingMock(false));
      dispatch(receiveScenarios({ scenarios: [] }));
      throw err;
    }
    dispatch(setUsingMock(true));
    dispatch(receiveScenarios(MOCK_SCENARIOS_RESPONSE));
    return MOCK_SCENARIOS_RESPONSE;
  }
};

/** GET /plans/{planId}/scenarios/{scenarioId}/submissions */
export const fetchScenarioSubmissions = (planId, scenarioId) => async (dispatch, getState) => {
  dispatch(requestScenarioSubmissions());
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();

  if (usingMock) {
    await delay();
    const payload = buildMockSubmissionsList(planId, scenarioId);
    dispatch(receiveScenarioSubmissions(payload));
    return payload;
  }

  const url = `${API_BASE}plans/${planId}/scenarios/${scenarioId}/submissions`;
  try {
    const res = await axios.get(url);
    const payload = normalizeSubmissionsPayload(res.data || {});
    dispatch(receiveScenarioSubmissions(payload));
    return payload;
  } catch (err) {
    dispatch(receiveScenarioSubmissions({ planId, scenarioId, submissions: [] }));
    if (isMockDisabled()) throw err;
    return { planId, scenarioId, submissions: [] };
  }
};

/** GET /plans/{planId}/scenario-submissions/{submissionId} */
export const fetchSubmissionDetail = (planId, submissionId) => async (dispatch, getState) => {
  dispatch(requestSubmissionDetail());
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();
  const isMockId = isMockSubmissionId(submissionId);

  if (usingMock || isMockId) {
    await delay();
    const detail = buildMockSubmissionDetail({
      planId,
      submissionId: Number(submissionId) || MOCK_SUBMISSION_ID,
    });
    dispatch(setUsingMock(true));
    dispatch(receiveSubmissionDetail(detail));
    return detail;
  }

  const url = `${API_BASE}plans/${planId}/scenario-submissions/${submissionId}`;
  try {
    const res = await axios.get(url);
    dispatch(receiveSubmissionDetail(res.data || null));
    return res.data;
  } catch (err) {
    if (isMockDisabled()) {
      dispatch(receiveSubmissionDetail(null));
      throw err;
    }
    const detail = buildMockSubmissionDetail({ planId, submissionId: MOCK_SUBMISSION_ID });
    dispatch(setUsingMock(true));
    dispatch(receiveSubmissionDetail(detail));
    return detail;
  }
};

/** POST /plans/{planId}/scenario-submissions */
export const createScenarioSubmission = (planId, data) => async (dispatch, getState) => {
  dispatch(setSubmissionRequest(true));
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();

  try {
    if (usingMock) {
      await delay();
      const scenarioId = data?.scenarioId || MOCK_SCENARIO_ID;
      return {
        submissionId: mockSubmissionIdForScenario(scenarioId),
        planId,
        scenarioId,
      };
    }

    const url = `${API_BASE}plans/${planId}/scenario-submissions`;
    const res = await axios.post(url, data);
    return res.data;
  } finally {
    dispatch(setSubmissionRequest(false));
  }
};

/** PATCH /plans/{planId}/scenario-submissions/{submissionId} */
export const updateScenarioSubmission = (planId, submissionId, data) => async (dispatch, getState) => {
  dispatch(setSubmissionRequest(true));
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();
  try {
    if (usingMock) {
      await delay();
      return { submissionId, planId, scenarioId: MOCK_SCENARIO_ID, ...data };
    }
    const url = `${API_BASE}plans/${planId}/scenario-submissions/${submissionId}`;
    const res = await axios.patch(url, data);
    return res.data;
  } finally {
    dispatch(setSubmissionRequest(false));
  }
};

/** PUT /plans/{planId}/scenario-submissions/{submissionId}/answers */
export const saveSubmissionAnswers = (planId, submissionId, answers) => async (dispatch, getState) => {
  dispatch(setSubmissionRequest(true));
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();
  try {
    if (usingMock) {
      await delay();
      const current = getState().testingExercisingReducer.submissionDetail;
      if (current) {
        const answerMap = {};
        (answers || []).forEach((item) => {
          answerMap[item.questionId] = item.answerText || '';
        });
        const next = {
          ...current,
          modifiedDate: new Date().toISOString(),
          videos: (current.videos || []).map((video) => ({
            ...video,
            questions: (video.questions || []).map((question) => ({
              ...question,
              answerText:
                answerMap[question.questionId] !== undefined
                  ? answerMap[question.questionId]
                  : question.answerText,
            })),
          })),
        };
        dispatch(receiveSubmissionDetail(next));
      }
      return {
        submissionId,
        status: current?.status || 'Draft',
        savedAnswerCount: (answers || []).length,
        modifiedDate: new Date().toISOString(),
      };
    }

    const url = `${API_BASE}plans/${planId}/scenario-submissions/${submissionId}/answers`;
    const res = await axios.put(url, { answers });
    return res.data;
  } finally {
    dispatch(setSubmissionRequest(false));
  }
};

/** POST /plans/{planId}/scenario-submissions/{submissionId}/submit */
export const submitScenarioSubmission = (planId, submissionId) => async (dispatch, getState) => {
  dispatch(setSubmissionRequest(true));
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();
  try {
    if (usingMock) {
      await delay();
      const current = getState().testingExercisingReducer.submissionDetail;
      const submittedDate = new Date().toISOString();
      if (current) {
        dispatch(
          receiveSubmissionDetail({
            ...current,
            status: 'Submitted',
            submittedDate,
            modifiedDate: submittedDate,
          }),
        );
      }
      return { submissionId, status: 'Submitted', submittedDate };
    }

    const url = `${API_BASE}plans/${planId}/scenario-submissions/${submissionId}/submit`;
    const res = await axios.post(url);
    return res.data;
  } finally {
    dispatch(setSubmissionRequest(false));
  }
};

/** POST /plans/{planId}/scenario-submissions/{submissionId}/cancel */
export const cancelScenarioSubmission = (planId, submissionId) => async (dispatch, getState) => {
  dispatch(setSubmissionRequest(true));
  const usingMock = getState().testingExercisingReducer.usingMock || isForcedMock();
  try {
    if (usingMock) {
      await delay();
      const cancelledDate = new Date().toISOString();
      return { submissionId, status: 'Cancelled', cancelledDate, modifiedDate: cancelledDate };
    }

    const url = `${API_BASE}plans/${planId}/scenario-submissions/${submissionId}/cancel`;
    const res = await axios.post(url);
    return res.data;
  } finally {
    dispatch(setSubmissionRequest(false));
  }
};
